import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { AuthCard } from './AuthCard';
import { CaptchaWidget } from './CaptchaWidget';
import { useSubmit } from './useSubmit';
import { auth } from '../../api/endpoints';
import { ApiError } from '../../api/problem';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { useT } from '../../i18n/provider';
import { REGISTRATION_OPEN } from '../../lib/registration';
import { useAuth } from '../../stores/auth';

export function LoginScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { busy, error, fields, submit } = useSubmit<{ access_token: string }>();
  const [mfaRequired, setMfaRequired] = useState(false);
  // El reto anti-bots resuelto, o nada mientras el widget trabaja. El backend
  // gasta cada reto al recibirlo, acierte o no la contraseña, así que
  // `captchaRound` remonta el widget —reto nuevo— tras cada respuesta.
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRound, setCaptchaRound] = useState(0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const code = String(form.get('mfa_code') ?? '');

    try {
      const result = await auth.login({
        email: String(form.get('email') ?? ''),
        password: String(form.get('password') ?? ''),
        ...(code ? { mfa_code: code } : {}),
        ...(captcha ? { captcha } : {}),
      });

      useAuth.getState().signIn(result.access_token, result.user);
      await navigate({ to: '/' });
    } catch (caught) {
      setCaptcha(null);
      setCaptchaRound((round) => round + 1);

      // The second factor is a step in the same form, not a second screen: the
      // password is already typed, and sending the user elsewhere to type six
      // digits would mean typing it again if they get them wrong.
      if (caught instanceof ApiError && caught.problem.type.endsWith('/mfa-required')) {
        setMfaRequired(true);

        return;
      }

      await submit(() => Promise.reject(caught));
    }
  };

  return (
    <AuthCard title={t('auth.signIn')}>
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
        {error ? <ErrorBanner error={error} /> : null}

        <Field label={t('auth.email')} name="email" type="email" autoComplete="email" required error={fields.email} />
        <Field
          label={t('auth.password')}
          name="password"
          type="password"
          autoComplete="current-password"
          required
          error={fields.password}
        />

        {mfaRequired ? (
          <Field
            label={t('auth.mfaCode')}
            name="mfa_code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            hint={t('auth.mfaHint')}
            error={fields.mfa_code}
          />
        ) : null}

        <CaptchaWidget key={captchaRound} onChange={setCaptcha} error={fields.captcha} />

        {/* Deshabilitado hasta tener el reto resuelto: enviar sin él sería un
            422 seguro, y el widget tarda menos de lo que se tarda en teclear
            la contraseña. */}
        <Button type="submit" busy={busy} disabled={captcha === null} size="lg" block>
          {mfaRequired ? t('auth.verify') : t('auth.signIn')}
        </Button>
      </form>

      {/* Sin alta pública no hay nada que ofrecer aquí, y `justify-end` deja
          el enlace de contraseña donde estaba en vez de centrarlo solo. */}
      <div className={REGISTRATION_OPEN ? 'flex justify-between text-sm' : 'flex justify-end text-sm'}>
        {REGISTRATION_OPEN ? (
          <Link to="/register" className="font-semibold text-primary transition-colors hover:text-primary-dark">
            {t('auth.createAccount')}
          </Link>
        ) : null}
        <Link to="/forgot-password" className="text-ink-soft transition-colors hover:text-ink">
          {t('auth.forgotPassword')}
        </Link>
      </div>
    </AuthCard>
  );
}
